<template>
  <div class="music-upload-container">
    <div class="gradient-background"></div>

    <div class="panel-container">
      <!-- 新增：左侧机器人列表面板 -->
      <div class="robot-list-panel">
        <div class="panel-header">
          <div class="title">{{$translate('robot.list')}}</div>
        </div>
        <div class="robot-list">
          <div
            class="robot-item"
            v-for="robot in robotList"
            :key="robot.id"
            :class="{ 'active': selectedRobotId === robot.id }"
            @click="selectRobot(robot)"
          >
            <div class="robot-info">
              <div class="robot-icon">
                <img src="@/assets/images/voice/Robot-icon.png" alt="robot" class="robot-img">
              </div>
              <div class="robot-details">
                <div class="robot-name">{{ robot.name }}</div>
                <div class="robot-serial">{{ robot.code }}</div>
              </div>
            </div>
            <div class="select-indicator" v-if="selectedRobotId === robot.id">
              <svg-icon name="check" class="check-icon"></svg-icon>
            </div>
          </div>
        </div>
        <!-- 修改分页组件部分 -->
        <div class="pagination">
          <div class="page-btn prev"
               @click="prevPage"
               :class="{ disabled: currentPage <= 1 }">
            <svg-icon name="arrow" class="page-icon arrow-left" />
          </div>

          <div class="page-info">
            <div class="current-page">{{ currentPage }}</div>
            <div class="page-divider"></div>
            <div class="total-page">{{ totalPages }}</div>
          </div>

          <div class="page-btn next"
               @click="nextPage"
               :class="{ disabled: currentPage >= totalPages }">
            <svg-icon name="arrow" class="page-icon arrow-right" />
          </div>
        </div>
      </div>

      <!-- 修改：音乐列表面板 -->
      <div class="music-list-panel">
        <div class="panel-header">
          <div class="title">{{ selectedRobotName || $translate('please_select_robot') }} - {{$translate('uploaded_music')}}</div>
        </div>

        <div class="music-list" v-if="selectedRobotId">
          <div
            class="music-item"
            v-for="(music, index) in uploadedMusic"
            :key="index"
          >
            <div class="music-info">
              <div class="music-icon">
                <svg-icon name="musicIcon" class="icon music-icon"></svg-icon>
              </div>
              <div class="music-details">
                <div class="music-name">{{ music.name }}</div>
                <!-- <div class="music-size">{{ formatFileSize(music.size) }}</div> -->
              </div>
            </div>
            <div class="music-actions">
              <div class="download-btn" @click="handleDownload(music)" v-show="music.relativePath"  :title="$translate('download')">
                <svg-icon name="download" class="download-icon"></svg-icon>
              </div>
              <div class="delete-btn" @click="deleteMusic(music,index)" :title="$translate('delete')">
                <svg-icon name="delete" class="delete-icon"></svg-icon>
              </div>
            </div>
          </div>
        </div>
        <div class="no-robot-selected" v-else>
          <div class="empty-message">{{$translate('please_select_robot')}}</div>
        </div>
      </div>

      <!-- 修改：右侧上传面板 -->
      <div class="floating-panel upload-panel">
        <!-- 修改面板头部显示选中的机器人 -->
        <div class="panel-header">
          <div class="left-title">{{$translate('music_upload')}}</div>
          <div class="header-robot">
            <img src="@/assets/images/voice/musicRobot.png" alt="Music Robot" class="floating-robot">
          </div>
        </div>

        <!-- 其他上传相关内容保持不变，但需要禁用未选择机器人时的上传功能 -->
        <div
          class="upload-area"
          :class="{
            'is-dragover': isDragover,
            'disabled': !selectedRobotId
          }"
          @dragover.prevent="selectedRobotId && handleDragover"
          @dragleave.prevent="selectedRobotId && handleDragleave"
          @drop.prevent="handleDrop($event)"
          @click="handleUploadClick"
        >
          <input
            type="file"
            ref="fileInput"
            accept=".mp3"
            multiple
            @change="handleFileChange"
            style="display: none"
          >
          <div class="upload-content">
            <div class="upload-icon">
              <svg-icon name="upload" class="icon icon-upload"></svg-icon>
            </div>
            <div class="upload-text">
              <p class="primary-text">{{$translate('drag_mp3_file_here')}}</p>
              <p class="secondary-text">{{$translate('or_click_to_select_file')}}</p>
            </div>
            <div class="file-types">{{$translate('only_support_mp3_format')}}</div>
          </div>
        </div>

        <!-- 文件列表 -->
        <div class="file-list" v-if="fileList.length > 0">
          <div
            class="file-item"
            v-for="(file, index) in fileList"
            :key="index"
            :class="{ 'upload-success': file.status === 'success' }"
          >
            <div class="file-info">
              <div class="file-icon">
                <svg-icon name="musicIcon" class="icon music-icon"></svg-icon>
              </div>
              <div class="file-details">
                <div class="file-name">{{ file.name }}</div>
                <div class="file-size">{{ formatFileSize(file.size) }}</div>
              </div>
            </div>

            <!-- 上传进度 -->
            <div class="upload-progress" v-if="file.status === 'uploading'">
              <div class="progress-bar">
                <div class="progress-inner" :style="{ width: file.progress + '%' }"></div>
              </div>
              <div class="progress-text">{{ file.progress }}%</div>
            </div>

            <!-- 状态图标 -->
            <div class="file-status">
              <template v-if="file.status === 'success'">
                <svg-icon name="success" class="status-icon success"></svg-icon>
              </template>
              <template v-else-if="file.status === 'error'">
                <svg-icon name="error" class="status-icon error"></svg-icon>
              </template>
              <template v-else>
                <div class="delete-btn" @click="removeFile(index)" :title="$translate('delete')">
                  <svg-icon name="delete" class="delete-icon"></svg-icon>
                </div>
              </template>
            </div>
          </div>
        </div>

        <!-- 上传按钮 -->
        <div class="upload-actions" v-show="fileList.length > 0">
          <div
            class="upload-btn"
            @click="startUpload"
            :class="{
              'disabled': isUploading || !selectedRobotId
            }"
          >
            <span>{{$translate('start_upload')}}</span>
            <div class="upload-wave" v-if="isUploading">
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
          <div class="clear-btn" @click="clearUploadList">
            <span>{{$translate('clear_list')}}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api'
var md5 = require("js-md5");
import { getSocketUrl } from '@/utils/websocket'

export default {
  data() {
    return {
      // 添加新的数据属性
      robotList: [],
      selectedRobotId: null,
      selectedRobotName: '',
      // 保留原有的数据属性
      isDragover: false,
      fileList: [],
      isUploading: false,
      uploadedMusic: [],
      downloadUrl: '',
      // 添加分页相关数据
      total: 0,
      currentPage: 1,
      pageSize: 15,
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.total / this.pageSize);
    }
  },
  methods: {
    // 新增：获取机器人列表
    async getRobotList() {
      try {
        const res = await api.getData(`robot-server/robot/list?index=${this.currentPage}`)
        if (res.state === 1) {
          this.robotList = res.data.rows || []
          this.total = res.data.total || 0
        } else {
          this.$Notice.error({
            title: this.$translate('tip'),
            desc: res.msg
          })
        }
      } catch (error) {
        this.$Notice.error({
          title: this.$translate('error'),
          desc: this.$translate('get_robot_list_error')
        })
      }
    },

    // 修改选择机器人的方法
    selectRobot(robot) {
      // 如果选择的是同一个机器人，不做处理
      if (this.selectedRobotId === robot.id) return;

      // 检查是否有未上传完成的文件
      const hasUnfinishedFiles = this.fileList.some(file => file.status !== 'success');

      if (hasUnfinishedFiles) {
        this.$Modal.confirm({
          title: this.$translate('switch_tip'),
          content: this.$translate('switch_tip_content'),
          okText: this.$translate('continue_switch'),
          cancelText: this.$translate('cancel'),
          onOk: () => {
            // 清空上传列表
            this.fileList = [];
            // 更新选中的机器人
            this.selectedRobotId = robot.id;
            this.selectedRobotName = robot.name;
            // 重新获取该机器人的音乐列表
            this.getUploadedMusic();
          }
        });
      } else {
        // 如果所有文件都已上传完成，直接切换并清空列表
        this.fileList = [];
        this.selectedRobotId = robot.id;
        this.selectedRobotName = robot.name;
        this.getUploadedMusic();
      }
    },

    // 修改：获取上传音乐列表，添加robotId参数
    getUploadedMusic() {
      if (!this.selectedRobotId) return

      api.getData(`notice-server/sound/list?robotId=${this.selectedRobotId}`).then((res) => {
        if (res.state === 0) {
          this.$Notice.error({
            title: this.$translate('tip'),
            desc: res.msg
          })
        } else {
          this.uploadedMusic = res.data.rows.map((item) => ({
            name: item.fileName,
            relativePath: this.downloadUrl + item.relativePath,
            id: item.id
          }))
        }
      })
    },

    // 修改：上传文件方法，添加robotId
    async startUpload() {
      if (this.isUploading) return
      if (!this.selectedRobotId) {
        this.$Message.warning(this.$translate('please_select_robot'))
        return
      }
      this.isUploading = true

      for (let item of this.fileList) {
        if (item.status === 'success') continue

        try {
          item.status = 'uploading'

          const fileMD5 = await this.calculateFileMD5(item.file)

          const formData = new FormData()
          formData.append('file', item.file)
          formData.append('fileMD5', fileMD5)
          formData.append('robotId', this.selectedRobotId) // 添加robotId

          const response = await api.upload('notice-server/sound/upload', formData, {
            onUploadProgress: (progressEvent) => {
              const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
              item.progress = progress
              this.$forceUpdate() // 强制更新视图
            }
          })

          if(response.state === 0){
            item.status = 'error'
            this.$Notice.error({
              title: this.$translate('upload_failed'),
              desc: `${item.name + this.$translate('upload_failed_desc')} : ${response.msg || this.$translate('unknown_error')}`
            })
          } else {
            this.getUploadedMusic()
            item.status = 'success'
            this.$Message.success(`${item.name + this.$translate('upload_success_desc')}`)
          }
        } catch (error) {
          item.status = 'error'
          this.$Notice.error({
            title: this.$translate('upload_failed'),
            desc: `${item.name + this.$translate('upload_failed_desc')} : ${error.message || this.$translate('unknown_error')}`
          })
        }

      }
      this.isUploading = false
    },

    handleDragover(e) {
      this.isDragover = true
    },
    handleDragleave(e) {
      this.isDragover = false
    },
    handleDrop(e) {
      e.preventDefault()
      if (!this.selectedRobotId) {
        this.$Message.warning(this.$translate('please_select_robot'))
        return
      }
      this.isDragover = false
      const files = Array.from(e.dataTransfer.files)
      this.handleFiles(files)
    },
    triggerFileInput() {
      this.$refs.fileInput.click()
    },
    handleFileChange(e) {
      if (!this.selectedRobotId) {
        this.$Message.warning(this.$translate('please_select_robot'))
        e.target.value = ''
        return
      }
      const files = Array.from(e.target.files)
      this.handleFiles(files)
      // 清空文件输入框的值，以便能重复选择相同的文件
      e.target.value = ''
    },
    async handleFiles(files) {
      const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB in bytes
      const existingFileNames = this.fileList.map(file => file.name);

      for (const file of files) {
        const isMp3Extension = file.name.toLowerCase().endsWith('.mp3');
        const isDuplicate = existingFileNames.includes(file.name);
        const isOverSize = file.size > MAX_FILE_SIZE;

        if (isDuplicate) {
          this.$Notice.warning({
            title: this.$translate('tip'),
            desc: `${file.name} ${this.$translate('already_in_upload_list')}`
          })
        }

        if (isOverSize) {
          this.$Notice.error({
            title: this.$translate('error'),
            desc: `${file.name} ${this.$translate('over_50mb_size_limit')}`
          })
        }

        if (!isMp3Extension) {
          this.$Notice.error({
            title: this.$translate('error'),
            desc: `${file.name} ${this.$translate('not_mp3_format_file')}`
          });
          continue;
        }

        try {
          // 验证文件是否为真实的MP3文件
          await this.validateMP3File(file);

          this.fileList.push({
            file,
            name: file.name,
            size: file.size,
            status: 'waiting',
            progress: 0
          });
        } catch (error) {
          this.$Notice.error({
            title: this.$translate('error'),
            desc: `${file.name} ${error.message}`
          });
        }
      }
    },
    removeFile(index) {
      this.fileList.splice(index, 1)
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },
    deleteMusic(item,index) {
      this.$Modal.confirm({
        title: this.$translate('confirm_delete'),
        content: this.$translate('confirm_delete_content'),
        onOk: () => {
          api.getData(`notice-server/sound/delete/${item.id}?robotId=${this.selectedRobotId}`).then((res)=>{
            if (res.state === 0) {
              this.$Notice.error({
                title: this.$translate('tip'),
                desc: res.msg
              });
            } else {
              // 从已上传列表中移除
              this.uploadedMusic.splice(index, 1);

              // 查找右侧上传列表中对应的文件
              console.log(this.fileList)
              const uploadedFile = this.fileList.find(file => file.name === item.name);
              if (uploadedFile) {
                // 重置文件状态
                uploadedFile.status = 'waiting';
                uploadedFile.progress = 0;

                // 将文件移到列表末尾
                const fileIndex = this.fileList.indexOf(uploadedFile);
                if (fileIndex > -1) {
                  this.fileList.splice(fileIndex, 1);
                  this.fileList.push(uploadedFile);
                }
              }

              this.$Message.success(this.$translate('delete_success'));
            }
          })
        }
      });
    },
    calculateFileMD5(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => {
          const md5Value = md5(e.target.result)
          resolve(md5Value)
        }
        reader.onerror = (error) => reject(error)
        reader.readAsArrayBuffer(file)
      })
    },
    simulateUpload(file) {
      return new Promise(resolve => {
        let progress = 0
        const interval = setInterval(() => {
          progress += Math.random() * 20
          if (progress > 100) {
            progress = 100
            clearInterval(interval)
            resolve()
          }
          file.progress = Math.floor(progress)
        }, 500)
      })
    },
    async handleDownload(music) {
      if (!music.relativePath) {
        this.$Message.error(this.$translate('download_address_not_exist'))
        return
      }
      return window.open(music.relativePath, '_blank')
    },
    clearUploadList() {
      this.$Modal.confirm({
        title: this.$translate('confirm_clear'),
        content: this.$translate('confirm_clear_content'),
        onOk: () => {
          this.fileList = [];
        }
      });
    },
    // 添加新的方法来验证MP3文件
    validateMP3File(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const uint8Array = new Uint8Array(e.target.result);

          // 检查文件头部的魔数
          // MP3文件通常以 ID3v2 标签（'ID3'）或 MPEG 同步标记（0xFF 0xFB 或 0xFF 0xFA）开始
          const isID3 = uint8Array[0] === 0x49 && uint8Array[1] === 0x44 && uint8Array[2] === 0x33;
          const isMPEG = uint8Array[0] === 0xFF && (uint8Array[1] === 0xFB || uint8Array[1] === 0xFA);

          if (isID3 || isMPEG) {
            resolve(true);
          } else {
            reject(new Error(this.$translate('not_mp3_format_file')));
          }
        };

        reader.onerror = () => reject(new Error(this.$translate('file_read_failed')));

        // 只读取文件的前10个字节进行验证
        const blob = file.slice(0, 10);
        reader.readAsArrayBuffer(blob);
      });
    },
    handleUploadClick() {
      if (!this.selectedRobotId) {
        this.$Message.warning(this.$translate('please_select_robot'))
        return
      }
      this.triggerFileInput()
    },
    // 上一页
    prevPage() {
      if (this.currentPage <= 1) return;
      this.handlePageChange(this.currentPage - 1);
    },

    // 下一页
    nextPage() {
      if (this.currentPage >= this.totalPages) return;
      this.handlePageChange(this.currentPage + 1);
    },

    // 修改原有的页码切换方法
    handlePageChange(page) {
      // 边界检查
      if (page < 1 || page > this.totalPages) return;

      this.currentPage = page;
      this.getRobotList(); // 重新获取机器人列表
    },
  },
  async created() {
    // 修改created钩子，先获取机器人列表
    await this.getRobotList()
    this.downloadUrl = getSocketUrl()+'/file'
  }
}
</script>

<style lang="less" scoped>
.music-upload-container {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.gradient-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    135deg,
    #0d1826 0%,
    #152033 25%,
    #1c2c45 50%,
    #152033 75%,
    #0d1826 100%
  );
  animation: gradientShift 15s ease infinite;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(
      circle at 50% 50%,
      rgba(140, 200, 255, 0.05) 0%,
      transparent 60%
    );
    pointer-events: none;
  }

  &:after {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: repeating-linear-gradient(
      transparent 0%,
      rgba(140, 200, 255, 0.03) 5%,
      transparent 10%
    );
    animation: lightEffect 10s linear infinite;
    transform: rotate(45deg);
  }
}

.panel-container {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 98%;
  height: 95%;
  display: flex;
  gap: 20px;
}

.robot-list-panel {
  flex: 0 0 20%;
  background: rgba(20, 30, 50, 0.5);
  backdrop-filter: blur(8px);
  border-radius: 12px;
  border: 1px solid rgba(140, 200, 255, 0.15);
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.15), 0 0 15px rgba(140, 200, 255, 0.1);

  .panel-header {
    padding: 20px;
    border-bottom: 1px solid rgba(140, 200, 255, 0.1);

    .title {
      font-size: 24px;
      font-weight: bold;
      color: #ffffff;
    }
  }
}

.robot-list {
  flex: 1;
  overflow-y: auto;
  padding: 16px;

  &::-webkit-scrollbar {
    width: 6px;
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(140, 200, 255, 0.2);
    border-radius: 3px;

    &:hover {
      background: rgba(140, 200, 255, 0.3);
    }
  }
}

.robot-item {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  padding-left: 6px;
  margin-bottom: 12px;
  background: rgba(28, 40, 60, 0.6);
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  border-left: 2px solid rgba(140, 200, 255, 0.3);

  &:hover {
    background: rgba(35, 50, 75, 0.7);
    border-left: 2px solid rgba(140, 200, 255, 0.6);
    box-shadow: 0 0 15px rgba(140, 200, 255, 0.15);
  }

  &.active {
    background: rgba(13, 60, 110, 0.5);
    border-left: 2px solid rgba(140, 200, 255, 0.8);
    box-shadow: 0 0 20px rgba(140, 200, 255, 0.15);

    .robot-name {
      color: #ffffff;
    }

    .robot-serial {
      color: rgba(255, 255, 255, 0.8);
    }
  }

  .robot-info {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;

    .robot-icon {
      width: 50px;
      height: 50px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;

      .robot-img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
    }

    .robot-details {
      display: flex;
      flex-direction: column;
      gap: 4px;

      .robot-name {
        color: rgba(255, 255, 255, 0.9);
        font-size: 16px;
        font-weight: 500;
      }

      .robot-serial {
        font-size: 13px;
        color: #ffca00;
      }
    }
  }

  .select-indicator {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #00bfff;
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    -webkit-box-align: center;
    -ms-flex-align: center;
    align-items: center;
    -webkit-box-pack: center;
    -ms-flex-pack: center;
    justify-content: center;
    -webkit-animation: check-appear-data-v-0e6db8c0 0.3s ease;
    animation: check-appear-data-v-0e6db8c0 0.3s ease;

    .check-icon {
      color: #fff;
      font-size: 20px;
    }
  }
}

.music-list-panel {
  flex: 0 0 30%;
  background: rgba(20, 30, 50, 0.5);
  backdrop-filter: blur(8px);
  border-radius: 12px;
  border: 1px solid rgba(140, 200, 255, 0.15);
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.15), 0 0 15px rgba(140, 200, 255, 0.1);

  .panel-header {
    padding: 20px;
    border-bottom: 1px solid rgba(140, 200, 255, 0.1);

    .title {
      font-size: 24px;
      font-weight: bold;
      color: #ffffff;
    }
  }

  .music-list {
    flex: 1;
    overflow-y: auto;
    padding: 20px;

    &::-webkit-scrollbar {
      width: 6px;
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(140, 200, 255, 0.2);
      border-radius: 3px;

      &:hover {
        background: rgba(140, 200, 255, 0.3);
      }
    }
  }
}

.music-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  margin-bottom: 12px;
  background: rgba(25, 40, 65, 0.7);
  border-radius: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(140, 200, 255, 0.15);

  &:hover {
    transform: translateY(-2px);
    background: rgba(30, 50, 80, 0.8);
    border-color: rgba(140, 200, 255, 0.3);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15), 0 0 15px rgba(140, 200, 255, 0.1);
  }

  .music-info {
    display: flex;
    align-items: center;
    flex: 1;

    .music-icon {
      width: 32px;
      height: 32px;
      border-radius: 6px;
      margin-right: 12px;

      .icon {
        width: 100%;
        height: 100%;
        color: #40c9ff;
      }
    }

    .music-details {
      display: flex;
      align-items: center;
      gap: 16px;

      .music-name {
        font-size: 16px;
        font-weight: 600;
        color: rgba(255, 255, 255, 0.9);
        margin-bottom: 0;
      }

      .music-size {
        font-size: 14px;
        font-weight: 500;
        color: rgba(255, 255, 255, 0.6);
      }
    }
  }

  .music-actions {
    display: flex;
    gap: 8px;

    .download-btn {
      width: 36px;
      height: 36px;
      border-radius: 8px;
      background: rgba(40, 70, 120, 0.7);
      border: 1px solid rgba(140, 200, 255, 0.4);
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.3s ease;

      &:hover {
        background: rgba(50, 85, 150, 0.8);
        border-color: rgba(140, 200, 255, 0.6);
        transform: translateY(-2px);
        box-shadow: 0 0 15px rgba(140, 200, 255, 0.2);

        .download-icon {
          transform: scale(1.1);
          color: #40c9ff;
        }
      }

      .download-icon {
        font-size: 20px;
        color: #40c9ff;
        transition: all 0.3s ease;
      }
    }

    .delete-btn {
      width: 36px;
      height: 36px;
      border-radius: 8px;
      background: rgba(40, 70, 120, 0.7);
      border: 1px solid rgba(140, 200, 255, 0.4);
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.3s ease;

      &:hover {
        background: rgba(50, 85, 150, 0.8);
        border-color: rgba(255, 100, 100, 0.6);
        transform: translateY(-2px);
        box-shadow: 0 0 15px rgba(255, 100, 100, 0.2);

        .delete-icon {
          transform: scale(1.1);
          color: #ff7875;
        }
      }

      .delete-icon {
        font-size: 20px;
        color: #ff7875;
        transition: all 0.3s ease;
      }
    }
  }
}

.upload-panel {
  flex: 0 0 45%;
  position: relative;
  height: 100%;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 35px 24px;

  .left-title, .right-title {
    font-size: 32px;
    font-weight: bold;
    color: #ffffff;
  }

  .header-robot {
    position: absolute;
    right: 0;
    width: 120px;
    height: 120px;
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0 20px;

    .floating-robot {
      width: 100%;
      height: 100%;
      object-fit: contain;
      animation: floatingRobot 3s ease-in-out infinite;
      filter: drop-shadow(0 0 10px rgba(140, 200, 255, 0.3));
    }
  }
}

/* 调整上传区域样式 */
.upload-area {
  margin: 0 20px 20px;
  height: 205px;
  min-height: 160px;
  border: 2px dashed rgba(140, 200, 255, 0.3);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: rgba(15, 25, 40, 0.3);

  &:hover, &.is-dragover {
    border-color: rgba(140, 200, 255, 0.6);
    background: rgba(25, 40, 65, 0.4);
    box-shadow: 0 0 20px rgba(140, 200, 255, 0.1);
  }

  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;

    &:hover {
      border-color: rgba(140, 200, 255, 0.3);
      background: rgba(15, 25, 40, 0.3);
      box-shadow: none;
    }
  }

  .upload-content {
    text-align: center;
    color: #fff;

    .upload-icon {
      font-size: 48px;
      opacity: 1;
      color: #40c9ff;
    }
    .icon-upload{
      height: 63px;
      width: 100px;
    }

    .primary-text {
      font-size: 20px;
      margin-bottom: 8px;
      color: rgba(255, 255, 255, 0.9);
    }

    .secondary-text {
      font-size: 16px;
      color: rgba(255, 255, 255, 0.7);
    }

    .file-types {
      margin-top: 16px;
      font-size: 14px;
      color: #40c9ff;
    }
  }
}

.file-list {
  flex: 1;
  overflow-y: auto;
  padding: 0 20px;
  margin-bottom: 20px;
  height: ~'calc(100% - 430px)';
  &::-webkit-scrollbar {
    width: 6px;
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(140, 200, 255, 0.2);
    border-radius: 3px;

    &:hover {
      background: rgba(140, 200, 255, 0.3);
    }
  }
}

.file-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  margin: 8px 0;
  background: rgba(25, 40, 65, 0.7);
  border-radius: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(140, 200, 255, 0.15);

  &:hover {
    transform: translateY(-2px);
    background: rgba(30, 50, 80, 0.8);
    border-color: rgba(140, 200, 255, 0.3);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15), 0 0 15px rgba(140, 200, 255, 0.1);
  }

  &.upload-success {
    background: rgba(20, 50, 80, 0.6);
    border: 1px solid rgba(140, 200, 255, 0.3);
    box-shadow: 0 0 15px rgba(140, 200, 255, 0.1);
  }

  .file-info {
    display: flex;
    align-items: center;
    flex: 1;

    .file-icon {
      width: 32px;
      height: 32px;
      border-radius: 6px;
      margin-right: 12px;

      .icon {
        font-size: 18px;
        color: #40c9ff;
      }
    }
    .music-icon{
      height: 100%;
      width: 100%;
      color: #40c9ff;
    }
    .file-details {
      display: flex;
      align-items: center;
      gap: 16px;

      .file-name {
        font-size: 16px;
        font-weight: 600;
        color: rgba(255, 255, 255, 0.9);
        margin-bottom: 0;
      }

      .file-size {
        font-size: 14px;
        font-weight: 500;
        color: rgba(255, 255, 255, 0.6);
      }
    }
  }
}

.file-status {
  .status-icon {
    font-size: 22px;

    &.success {
      color: #52c41a;
      filter: drop-shadow(0 2px 4px rgba(82, 196, 26, 0.3));
    }

    &.error {
      color: #ff4d4f;
      filter: drop-shadow(0 2px 4px rgba(255, 77, 79, 0.3));
    }
  }

  .delete-btn {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: rgba(40, 70, 120, 0.7);
    border: 1px solid rgba(140, 200, 255, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      background: rgba(50, 85, 150, 0.8);
      border-color: rgba(255, 100, 100, 0.6);
      transform: translateY(-2px);
      box-shadow: 0 0 15px rgba(255, 100, 100, 0.2);

      .delete-icon {
        transform: scale(1.1);
        color: #ff7875;
      }
    }

    .delete-icon {
      font-size: 20px;
      color: #ff7875;
      transition: all 0.3s ease;
    }
  }
}

.upload-progress {
  margin: 0 12px;

  .progress-bar {
    width: 100px;
    height: 4px;
    background: rgba(15, 25, 40, 0.5);
    border-radius: 3px;
    overflow: hidden;
    margin-right: 10px;
    box-shadow: inset 2px 2px 5px rgba(0, 0, 0, 0.1);

    .progress-inner {
      height: 100%;
      background: linear-gradient(90deg, #36cfc9, #40a9ff);
      box-shadow: 0 0 8px rgba(64, 169, 255, 0.5);
      transition: width 0.3s ease;
    }
  }

  .progress-text {
    color: rgba(255, 255, 255, 0.9);
    font-size: 12px;
    font-weight: 600;
  }
}

.upload-actions {
  padding: 20px;
  display: flex;
  justify-content: center;
  gap: 20px;

  .upload-btn {
    padding: 12px 40px;
    background: linear-gradient(45deg, #1890ff, #096dd9);
    border: 1px solid rgba(140, 200, 255, 0.5);
    border-radius: 25px;
    color: #fff;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    box-shadow: 0 2px 8px rgba(24, 144, 255, 0.3);

    &:hover:not(.disabled) {
      transform: translateY(-2px);
      box-shadow: 0 4px 15px rgba(24, 144, 255, 0.4);
      background: linear-gradient(45deg, #40a9ff, #1890ff);
    }

    &.disabled {
      opacity: 0.7;
      cursor: not-allowed;
      background: linear-gradient(45deg, #5a6d82, #364d63);

      &:hover {
        transform: none;
        box-shadow: none;
      }
    }

    .upload-wave {
      display: flex;
      align-items: center;
      margin-left: 12px;
      height: 20px;

      span {
        display: inline-block;
        width: 3px;
        height: 100%;
        margin: 0 2px;
        background: #fff;
        border-radius: 3px;
        animation: wave 1.2s ease-in-out infinite;

        &:nth-child(1) { animation-delay: 0s; }
        &:nth-child(2) { animation-delay: 0.3s; }
        &:nth-child(3) { animation-delay: 0.6s; }
        &:nth-child(4) { animation-delay: 0.9s; }
      }
    }
  }

  .clear-btn {
    padding: 12px 40px;
    background: rgba(40, 70, 120, 0.7);
    border: 1px solid rgba(140, 200, 255, 0.4);
    border-radius: 25px;
    color: #fff;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
      background: rgba(50, 85, 150, 0.8);
      border-color: rgba(140, 200, 255, 0.6);
    }
  }
}

@keyframes wave {
  0%, 100% { height: 8px; }
  50% { height: 20px; }
}

@keyframes gradientShift {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

@keyframes lightEffect {
  0% { transform: rotate(45deg) translateY(0); }
  100% { transform: rotate(45deg) translateY(-50%); }
}

/* 优化浮动动画 */
@keyframes floatingRobot {
  0% {
    transform: translateY(0) scale(1);
    opacity: 0.9;
  }
  50% {
    transform: translateY(-15px) scale(1.05);
    opacity: 0.7;
  }
  100% {
    transform: translateY(0) scale(1);
    opacity: 0.9;
  }
}

.no-robot-selected {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;

  .empty-message {
    color: rgba(140, 200, 255, 0.6);
    font-size: 18px;
  }
}

.pagination {
  padding: 16px;
  border-top: 1px solid rgba(140, 200, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;

  .page-btn {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: rgba(40, 70, 120, 0.7);
    border: 1px solid rgba(140, 200, 255, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover:not(.disabled) {
      background: rgba(50, 85, 150, 0.8);
      transform: scale(1.05);
      border-color: rgba(140, 200, 255, 0.6);
      box-shadow: 0 0 15px rgba(140, 200, 255, 0.2);
    }

    &.disabled {
      opacity: 0.5;
      cursor: not-allowed;

      &:hover {
        background: rgba(40, 70, 120, 0.7);
        transform: none;
        box-shadow: none;
      }
    }

    .page-icon {
      font-size: 20px;
      color: #40c9ff;

      &.arrow-right {
        transform: rotate(180deg);
      }
    }
  }

  .page-info {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #fff;
    font-size: 16px;

    .current-page, .total-page {
      min-width: 24px;
      text-align: center;
      opacity: 0.8;
    }

    .page-divider {
      width: 20px;
      height: 2px;
      background: linear-gradient(90deg, transparent 0%, rgba(140, 200, 255, 0.6) 50%, transparent 100%);
      border-radius: 1px;
    }
  }
}
</style>

